import { useState, useEffect } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import OnboardingTourService from 'services/OnboardingTourService'
import { ONBOARDING_STEP_CONFIG } from 'constants/onboarding.constant'
import { hasCarPortalMinimalPermissionsSelector } from 'store/auth/userSlice'

const useOnboardingTour = () => {
    const [isActive, setIsActive] = useState(false)
    const [currentStep, setCurrentStep] = useState(1)
    const [pendingStep, setPendingStep] = useState(null)
    const [onboardingStatus, setOnboardingStatus] = useState(null)
    const [hasCheckedOnboarding, setHasCheckedOnboarding] = useState(false)
    const [showCompletionModal, setShowCompletionModal] = useState(false)
    const location = useLocation()
    const navigate = useNavigate()
    const mediatorId = useSelector((state) => state.auth.user?.mediator_id)
    const isCarPortal = useSelector((state) => state.auth.user?.is_car_portal || false)
    // Tour only when user is authenticated. On page reload ProtectedRoute runs validate API first;
    // user is set only after validate succeeds, so this gate ensures tour runs only after validate response is true.
    const isAuthenticated = useSelector((state) => state.auth.user !== null)
    const hasMinimalPermissions = useSelector(
        hasCarPortalMinimalPermissionsSelector
    )

    useEffect(() => {
        const loadOnboardingStatus = async () => {
            // Tour only for is_car_portal: skip for non-CAR users once we know the user is loaded
            if (!isCarPortal) {
                // Only mark as checked when we're sure user is loaded and is not CAR, so we don't retry.
                // If user might not be loaded yet (e.g. first login), keep hasCheckedOnboarding false so we retry when state updates.
                if (isAuthenticated && mediatorId != null) {
                    setHasCheckedOnboarding(true)
                }
                return
            }
            // Hide tour until full authorized: skip tour for minimal-permission users
            if (hasMinimalPermissions) {
                setHasCheckedOnboarding(true)
                return
            }
            // Explicit gate: tour only when authenticated (validate API has succeeded and user is set)
            if (!mediatorId || !isAuthenticated) {
                setHasCheckedOnboarding(false)
                return
            }

            try {
                const status = await OnboardingTourService.getOnboardingStatus(mediatorId)
                setOnboardingStatus(status)
                setHasCheckedOnboarding(true)

                // When full authorized: if tour is not done, show the tour
                if (OnboardingTourService.shouldShowOnboarding(status)) {
                    setCurrentStep(status.currentStep)
                    setTimeout(() => {
                        setIsActive(true)
                    }, 500)
                }
            } catch (error) {
                console.error('Error loading onboarding status:', error)
                setHasCheckedOnboarding(true)
            }
        }

        if (isAuthenticated && !hasCheckedOnboarding) {
            loadOnboardingStatus()
        }
    }, [mediatorId, isAuthenticated, hasCheckedOnboarding, hasMinimalPermissions, isCarPortal])

    const startTour = () => {
        if (!isCarPortal || hasMinimalPermissions) return
        if (onboardingStatus && !onboardingStatus.completed) {
            setIsActive(true)
            setCurrentStep(onboardingStatus.currentStep || 1)
        }
    }

    const stopTour = () => {
        setIsActive(false)
    }

    const handleStepChange = async (newStep) => {
        const nextConfig = ONBOARDING_STEP_CONFIG[newStep]
        const routeChanged = nextConfig && location.pathname !== nextConfig.route
        const sameRouteNeedsState =
            nextConfig?.routeState &&
            location.pathname === nextConfig.route

        if (routeChanged || sameRouteNeedsState) {
            setPendingStep(newStep)
            navigate(nextConfig.route, {
                state: nextConfig.routeState || {},
                replace: !!sameRouteNeedsState,
            })
        } else {
            setCurrentStep(newStep)
            if (mediatorId) {
                await OnboardingTourService.updateStep(mediatorId, newStep)
            }
        }
    }

    // After navigation: wait for pending step's element to be rendered, then show that step and scroll to it
    useEffect(() => {
        if (pendingStep == null) return
        const config = ONBOARDING_STEP_CONFIG[pendingStep]
        if (!config || location.pathname !== config.route) return

        const selector = config.subSteps?.[0]?.element ?? config.element
        let attempts = 0
        const maxAttempts = 50
        const intervalMs = 300

        const check = () => {
            const el = document.querySelector(selector)
            const ready = el && el.getBoundingClientRect().width > 0 && el.getBoundingClientRect().height > 0
            if (ready) {
                setCurrentStep(pendingStep)
                setPendingStep(null)
                if (mediatorId) {
                    OnboardingTourService.updateStep(mediatorId, pendingStep)
                }
                el.scrollIntoView({ behavior: 'smooth', block: 'center' })
                return
            }
            attempts += 1
            if (attempts < maxAttempts) {
                setTimeout(check, intervalMs)
            } else {
                setCurrentStep(pendingStep)
                setPendingStep(null)
            }
        }
        const t = setTimeout(check, 100)
        return () => clearTimeout(t)
    }, [pendingStep, location.pathname, mediatorId])

    const handleComplete = async () => {
        if (mediatorId) {
            await OnboardingTourService.completeOnboarding(mediatorId)
        }
        setIsActive(false)
        setOnboardingStatus((prev) => ({
            ...prev,
            completed: true,
            currentStep: 0,
        }))
        setShowCompletionModal(true)
    }

    const closeCompletionModal = () => {
        setShowCompletionModal(false)
    }

    const handleSkip = async () => {
        if (mediatorId) {
            await OnboardingTourService.skipOnboarding(mediatorId)
        }
        setIsActive(false)
        setOnboardingStatus((prev) => ({
            ...prev,
            completed: true,
            currentStep: 0,
        }))
    }

    return {
        isActive,
        currentStep,
        pendingStep,
        onboardingStatus,
        showCompletionModal,
        closeCompletionModal,
        startTour,
        stopTour,
        handleStepChange,
        handleComplete,
        handleSkip,
    }
}

export default useOnboardingTour
